<template>
  <div class="upd-view" data-testid="upd-readable-body">
    <upd-paper-document ref="paper" :form="body" :date-value="documentDate" :summary="summary" readonly :measure-options="measureOptions" :vat-options="vatOptions" />

    <vat-registration-panel :document="documentMeta" :history-id="$route.params.history_id || 'last'" @reload="load" />

    <details v-if="extraEntries.length" class="upd-view__extra" data-testid="upd-legacy-fields">
      <summary>Дополнительные сохраненные реквизиты</summary>
      <dl><div v-for="entry in extraEntries" :key="entry.key"><dt>{{ entry.key }}</dt><dd>{{ display(entry.value) }}</dd></div></dl>
    </details>
  </div>
</template>

<script>
import moment from 'moment'
import UpdPaperDocument from '../../../../components/documents/upd/UpdPaperDocument.vue'
import VatRegistrationPanel from '../../../../components/documents/VatRegistrationPanel.vue'
import { calculateUpdSummary, normalizeUpdDocumentBody, updMeasureOptions, updVatOptionsForRows } from '../../../../services/updDocument'

const mappedFields = new Set([
  'accept_vat_deduction', 'nds_deduction', 'accepted_date', 'acceptance_date', 'date_accepted',
  'summary', 'is_service', 'document_direction', 'counterparty_id', 'counterparty_snapshot', 'cust', 'cust_adr', 'cust_inn', 'currency', 'goscon', 'seller', 'seller_adr', 'seller_inn', 'seller_sender', 'seller_receiver', 'seller_doc', 'seller_send_doc', 'payment_reference', 'plat_ras', 'advance_invoice_info', 'has_advance_offset', 'change_n', 'change_date', 'status', 'function', 'report_type', 'signatory', 'rukvod', 'glavbuh', 'ip_name', 'ip_req', 'poa', 'osn_rec_sent', 'transp', 'goods_sent', 'goods_sent_workplace', 'goods_sent_fio', 'date_sent', 'sent_info', 'hozvod_workplace', 'hozvod_fio', 'econom_name', 'date_rec', 'goods_rec_fio', 'goods_rec_workplace', 'rec_info', 'econom_rec_name', 'hozvod_rec_fio', 'hozvod_rec_workplace', 'table_rows', 'in_org_number', 'document_date', 'document_type'
])

export default {
  name: 'document_info_upd',
  components: { UpdPaperDocument, VatRegistrationPanel },
  props: { embeddedWorkbench: { type: Boolean, default: false } },
  data() { return { body: normalizeUpdDocumentBody({}), documentMeta: {}, handlers: [], requestId: null } },
  computed: {
    rows() { return this.body.table_rows || [] },
    summary() { return { ...calculateUpdSummary(this.rows), ...(this.body.summary || {}) } },
    measureOptions() { return updMeasureOptions },
    vatOptions() { return updVatOptionsForRows(this.rows) },
    functionLabel() { return Number(this.body.status) === 2 ? 'Передаточный документ (акт)' : 'Счет-фактура и передаточный документ' },
    documentDate() { const date = moment(this.body.document_date); return date.isValid() ? date.format('DD.MM.YYYY') : displayValue(this.body.document_date) },
    correctionLabel() { return this.body.change_n || this.body.change_date ? [this.body.change_n ? `№ ${this.body.change_n}` : '', this.body.change_date ? `от ${this.body.change_date}` : ''].filter(Boolean).join(' ') : 'Без исправлений' },
    traceabilityRows() { return this.rows.map((row, index) => ({ row, index })).filter(({ row }) => row.doc || row.trace_unit_code || row.trace_quantity || row.trace_cost_without_nds) },
    fulfilmentFacts() {
      return [
        ['Основание передачи / получения', this.body.osn_rec_sent], ['Транспортировка и груз', this.body.transp], ['Передал', [this.body.goods_sent_workplace, this.body.goods_sent_fio].filter(Boolean).join(', ')], ['Дата передачи', this.body.date_sent], ['Сведения о передаче', this.body.sent_info], ['Ответственный за оформление', [this.body.hozvod_workplace, this.body.hozvod_fio].filter(Boolean).join(', ')], ['Принял', [this.body.goods_rec_workplace, this.body.goods_rec_fio].filter(Boolean).join(', ')], ['Дата приемки', this.body.date_rec], ['Сведения о приемке', this.body.rec_info], ['Ответственный за приемку', [this.body.hozvod_rec_workplace, this.body.hozvod_rec_fio].filter(Boolean).join(', ')], ['Составитель продавца', this.body.econom_name], ['Составитель покупателя', this.body.econom_rec_name]
      ].map(([label, value]) => ({ label, value }))
    },
    extraEntries() { return Object.entries(this.body).filter(([key, value]) => !mappedFields.has(key) && value !== null && value !== '' && value !== undefined).map(([key, value]) => ({ key, value })) }
  },
  mounted() {
    this.listen('didReceiveDocumentInfo', this.onDocument)
    this.listen('connectionReady', this.load)
    if (this.connection.isAuthenticated) this.load()
  },
  beforeUnmount() { this.handlers.forEach(([event, handler]) => this.emitter.off(event, handler)) },
  methods: {
    listen(event, handler) { this.emitter.on(event, handler); this.handlers.push([event, handler]) },
    load() { this.requestId = this.connection.makeRequestId('upd-readable'); this.connection.getDocumentInfo(this.requestId, this.$route.params.id, this.$route.params.history_id) },
    onDocument(response) {
      if (response.request_id && response.request_id !== this.requestId) return
      if (!response.data || response.data.document_type !== 'upd') return
      this.documentMeta = response.data
      this.body = normalizeUpdDocumentBody(response.data.body || {})
    },
    display: displayValue,
    optionLabel(value) { return value && typeof value === 'object' ? (value.label || value.value || '—') : (value || '—') },
    traceabilityLabel(row) { return [`РНПТ/декларация: ${row.doc || '—'}`, `ед.: ${row.trace_unit_code || '—'} ${row.trace_unit_name || ''}`, `кол-во: ${row.trace_quantity || '—'}`, `стоимость без НДС: ${row.trace_cost_without_nds || '—'}`].join(' · ') },
    focusArtifactDiagnostic(diagnostic) {
      const fieldPath = normalizeDiagnosticPath(diagnostic && diagnostic.fieldPath)
      const lineIndex = diagnosticLineIndex(diagnostic, fieldPath)
      let candidates = []
      const fields = Array.from(this.$el.querySelectorAll('[data-field-path]'))
      if (fieldPath) candidates = fields.filter((node) => node.dataset.fieldPath === fieldPath)
      if (!candidates.length && lineIndex !== null) candidates = fields.filter((node) => node.dataset.fieldPath.startsWith(`table_rows[${lineIndex}].`))
      if (!candidates.length) {
        const sectionId = diagnosticSectionId(fieldPath)
        const section = sectionId && this.$el.querySelector(`#${sectionId}`)
        if (section) candidates = [section.closest('section') || section]
      }
      const target = candidates.find((node) => node.getClientRects().length) || candidates[0] || this.$el
      const focusable = target.querySelector('input, textarea, [tabindex]:not([tabindex="-1"])') || target
      if (!focusable.hasAttribute('tabindex') && !focusable.matches('input, textarea')) focusable.setAttribute('tabindex', '-1')
      target.scrollIntoView({ block: 'center' })
      focusable.focus({ preventScroll: true })
    },
    onDeleteDocument() { this.connection.deleteDocument(this.connection.makeRequestId('upd-delete'), this.$route.params.id) },
    onRestoreDocument() { this.connection.restoreDocument(this.connection.makeRequestId('upd-restore'), this.$route.params.id) }
  }
}

function displayValue(value) {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value !== 'object') return String(value)
  if (Array.isArray(value)) return value.map(displayValue).join('; ')
  const reference = [value.name, value.number ? `№ ${value.number}` : '', value.date ? `от ${value.date}` : ''].filter(Boolean).join(' ')
  return reference || Object.entries(value).map(([key, item]) => `${key}: ${displayValue(item)}`).join('; ')
}

function normalizeDiagnosticPath(value) {
  const path = String(value || '').replace(/^document\./, '')
  const aliases = {
    'seller.inn': 'seller_inn',
    'seller.address': 'seller_adr',
    'buyer.name': 'cust',
    'buyer.inn': 'cust_inn',
    'buyer.address': 'cust_adr',
    'customer.name': 'cust',
    'customer.inn': 'cust_inn',
    'customer.address': 'cust_adr',
    'signatories.fio': 'rukvod',
    'signatories.position': 'rukvod',
    'invoice.advance': 'advance_invoice_info'
  }
  return aliases[path] || path
}

function diagnosticLineIndex(diagnostic, fieldPath) {
  if (Number.isInteger(diagnostic && diagnostic.lineIndex)) return diagnostic.lineIndex
  const match = String(fieldPath || '').match(/(?:table_rows|lines|rows)\[(\d+)\]/)
  return match ? Number(match[1]) : null
}

function diagnosticSectionId(fieldPath) {
  if (/^(seller|cust|buyer|customer)/.test(fieldPath)) return 'upd-view-parties'
  if (/advance|payment|plat_|seller_doc|seller_send_doc|change_|goscon|poa/.test(fieldPath)) return 'upd-view-references'
  if (/table_rows|lines|rows|trace/.test(fieldPath)) return 'upd-view-lines'
  if (/goods_|date_|sent_|rec_|transp|hozvod|econom|osn_/.test(fieldPath)) return 'upd-view-delivery'
  if (/rukvod|glavbuh|ip_|signature/.test(fieldPath)) return 'upd-view-signatures'
  return ''
}
</script>

<style scoped>
.upd-view { display: grid; gap: var(--sid-space-5); min-width: 0; color: var(--sid-color-text-primary); }
.upd-view__header { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--sid-space-5); padding-bottom: var(--sid-space-4); border-bottom: 1px solid var(--sid-color-line); }
.upd-view__eyebrow { margin: 0 0 var(--sid-space-1); color: var(--sid-color-text-secondary); font-size: .75rem; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
.upd-view__header h2, .upd-view__header p { margin: 0; }
.upd-view__header h2 { font-size: 1.5rem; }
.upd-view__header h2 + p { margin-top: var(--sid-space-1); color: var(--sid-color-text-secondary); }
.upd-view__total { display: grid; justify-items: end; }
.upd-view__total span { color: var(--sid-color-text-secondary); font-size: .75rem; }
.upd-view__total :deep(.amount-cell) { font-size: 1.25rem; font-weight: 700; }
.upd-view__section { min-width: 0; padding: var(--sid-space-4); border: 1px solid var(--sid-color-line); border-radius: var(--sid-radius-surface); background: var(--sid-color-surface-0); }
.upd-view__section h3, .upd-view__section p { margin: 0; }
.upd-view__section > h3 { margin-bottom: var(--sid-space-4); font-size: 1rem; }
.upd-view__tax { display: grid; grid-template-columns: minmax(0, 1fr) minmax(240px, 360px); align-items: center; gap: var(--sid-space-5); }
.upd-view__tax h3, .upd-view__tax p { margin: 0; }
.upd-view__tax p { margin-top: var(--sid-space-1); color: var(--sid-color-text-secondary); }
.upd-view__parties { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--sid-space-4); }
.upd-view__parties article { display: grid; gap: var(--sid-space-1); }
.upd-view__parties span, .upd-view__parties p { color: var(--sid-color-text-secondary); }
.upd-view__facts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 var(--sid-space-5); margin: var(--sid-space-4) 0 0; }
.upd-view__facts--three { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.upd-view__facts div { padding: var(--sid-space-3) 0; border-top: 1px solid var(--sid-color-line); }
.upd-view__facts dt { color: var(--sid-color-text-secondary); font-size: .75rem; }
.upd-view__facts dd { margin: var(--sid-space-1) 0 0; overflow-wrap: anywhere; }
.upd-view__section-heading { display: flex; justify-content: space-between; gap: var(--sid-space-4); margin-bottom: var(--sid-space-4); }
.upd-view__section-heading p { color: var(--sid-color-text-secondary); }
.upd-view__summary { display: flex; gap: var(--sid-space-4); }
.upd-view__summary span { white-space: nowrap; color: var(--sid-color-text-secondary); }
.upd-view__table-scroll { overflow: auto; border: 1px solid var(--sid-color-line); border-radius: var(--sid-radius-control); }
.upd-view__table { width: 100%; min-width: 760px; border-collapse: collapse; }
.upd-view__table th, .upd-view__table td { padding: 10px 12px; text-align: left; border-bottom: 1px solid var(--sid-color-line); vertical-align: top; }
.upd-view__table th { color: var(--sid-color-text-secondary); font-size: .75rem; background: var(--sid-color-surface-subtle); }
.upd-view__table td:nth-last-child(-n+3) { text-align: right; }
.upd-view__table small { display: block; margin-top: 3px; color: var(--sid-color-text-secondary); }
.upd-view__line-cards { display: none; }
.upd-view__details { margin-top: var(--sid-space-4); }
.upd-view__details summary { cursor: pointer; font-weight: 600; }
.upd-view [data-artifact-path]:focus, .upd-view [data-artifact-line-index]:focus, .upd-view__section:focus { outline: var(--sid-focus-width) solid var(--sid-focus-color); outline-offset: var(--sid-focus-offset); }
@media (max-width: 900px) { .upd-view__facts--three { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 767px) {
  .upd-view__header, .upd-view__section-heading { align-items: stretch; flex-direction: column; }
  .upd-view__total { justify-items: start; }
  .upd-view__parties, .upd-view__facts, .upd-view__facts--three { grid-template-columns: 1fr; }
  .upd-view__tax { grid-template-columns: 1fr; }
  .upd-view__summary { justify-content: space-between; }
  .upd-view__table-scroll { display: none; }
  .upd-view__line-cards { display: grid; gap: var(--sid-space-2); }
  .upd-view__line-cards article { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; gap: var(--sid-space-3); align-items: center; padding: var(--sid-space-3); border: 1px solid var(--sid-color-line); border-radius: var(--sid-radius-control); }
  .upd-view__line-cards p { color: var(--sid-color-text-secondary); }
}
</style>
